import React, { useMemo, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Phone, Mail, MapPin, ShieldAlert, ArrowRight } from "lucide-react";
import useSiteConfig from "@/hooks/useSiteConfig";
import { LOCALE_KEY, SUPPORTED_LOCALES } from "@/i18n";

const DEFAULTS = {
  headline: {
    hr: "Stranica u izradi",
    en: "Site under construction",
    de: "Webseite im Aufbau",
    fr: "Site en construction",
  },
  message: {
    hr: "Naša nova web-stranica uskoro stiže. Za upite slobodno nas kontaktirajte.",
    en: "Our new website is launching soon. Get in touch in the meantime.",
    de: "Unsere neue Website wird in Kürze veröffentlicht. Bitte kontaktieren Sie uns in der Zwischenzeit.",
    fr: "Notre nouveau site sera bientôt en ligne. N'hésitez pas à nous contacter entre-temps.",
  },
  eyebrow: { hr: "U izradi", en: "Coming back soon", de: "In Bearbeitung", fr: "Bientôt de retour" },
  reach: { hr: "Kontakt", en: "Reach us", de: "Kontakt", fr: "Contact" },
  adminLink: { hr: "Admin prijava", en: "Admin login", de: "Admin-Login", fr: "Connexion admin" },
};

function pickLocaleFromPath(pathname) {
  const m = pathname.match(/^\/(hr|en|de|fr)(\/|$)/);
  if (m) return m[1];
  try {
    const stored = localStorage.getItem(LOCALE_KEY);
    if (stored && SUPPORTED_LOCALES.includes(stored)) return stored;
  } catch { /* noop */ }
  return "hr";
}

/**
 * Full-viewport cover page shown to anonymous visitors when maintenance mode is ON.
 * Does NOT use PublicLayout (intentionally) so the gate stays simple and the cover
 * can never accidentally trigger broken nav links to disabled pages.
 */
export default function UnderConstruction() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const config = useSiteConfig();
  const [locale, setLocale] = useState(() => pickLocaleFromPath(location.pathname));

  useEffect(() => {
    // Persist + propagate locale switch
    try { localStorage.setItem(LOCALE_KEY, locale); } catch { /* noop */ }
    i18n.changeLanguage(locale).catch(() => {});
  }, [locale, i18n]);

  const headline = useMemo(() => {
    const fromDb = config?.maintenance_headline || {};
    return fromDb[locale] || fromDb.hr || DEFAULTS.headline[locale] || DEFAULTS.headline.hr;
  }, [config, locale]);
  const message = useMemo(() => {
    const fromDb = config?.maintenance_message || {};
    return fromDb[locale] || fromDb.hr || DEFAULTS.message[locale] || DEFAULTS.message.hr;
  }, [config, locale]);

  const phoneL = config?.business_phone_landline_tel || "+38542262032";
  const phoneM = config?.business_phone_mobile_tel || "+38598284876";
  const phoneLDisplay = config?.business_phone_landline || "042 262 032";
  const phoneMDisplay = config?.business_phone_mobile || "098 284 876";
  const email = config?.business_email || "tamburezmegac@gmail.com";
  const addr = [
    config?.business_address_line || "Ivana Rangera 44",
    `${config?.business_address_postal || "42000"} ${config?.business_address_city || "Varaždin"}`,
    config?.business_address_country || "Hrvatska",
  ];

  // Update document title + robots meta + html lang imperatively (Helmet has
  // strict child-shape rules that conflict with our dynamic content; this is
  // smaller and more reliable for a single cover page.)
  useEffect(() => {
    document.title = `${headline} — Tambure Žmegač`;
    document.documentElement.lang = locale;
    let m = document.querySelector('meta[name="robots"]');
    if (!m) {
      m = document.createElement("meta");
      m.name = "robots";
      document.head.appendChild(m);
    }
    m.content = "noindex, nofollow";
    return () => {
      // Restore indexable defaults if the user leaves the cover (e.g. admin
      // logs in via the discreet footer link)
      if (m) m.content = "index, follow";
    };
  }, [headline, locale]);

  return (
    <div data-testid="under-construction" className="min-h-screen bg-tz-cream text-tz-espresso flex flex-col">

      {/* Top bar with language switcher */}
      <header className="px-5 lg:px-10 pt-6 flex items-center justify-between">
        <Link to="/" className="font-serif text-2xl tracking-wide" data-testid="uc-wordmark" aria-label="Tambure Žmegač">
          <span className="text-tz-espresso/85">tambure</span>{" "}
          <span className="text-primary font-medium uppercase">Žmegač</span>
        </Link>
        <div className="flex items-center gap-1.5" data-testid="uc-locale-switcher" role="group" aria-label="Language">
          {SUPPORTED_LOCALES.map((loc) => (
            <button
              key={loc}
              type="button"
              data-testid={`uc-locale-${loc}`}
              onClick={() => setLocale(loc)}
              className={`text-xs uppercase tracking-[0.18em] px-2.5 py-1.5 rounded-md transition-colors ${
                loc === locale
                  ? "bg-primary text-primary-foreground"
                  : "text-tz-espresso/55 hover:text-primary"
              }`}
            >
              {loc}
            </button>
          ))}
        </div>
      </header>

      {/* Hero block — generous whitespace, asymmetric, on-brand */}
      <main className="flex-1 grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 px-5 lg:px-10 py-10 lg:py-16 max-w-7xl mx-auto w-full items-center">
        <section className="space-y-6 lg:space-y-8">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-primary">
            <ShieldAlert className="h-4 w-4" strokeWidth={1.5} />
            <span data-testid="uc-eyebrow">{DEFAULTS.eyebrow[locale] || DEFAULTS.eyebrow.hr}</span>
          </div>
          <h1
            data-testid="uc-headline"
            className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-tz-espresso"
          >
            {headline}
          </h1>
          <p data-testid="uc-message" className="text-lg lg:text-xl text-tz-espresso/75 leading-relaxed max-w-prose">
            {message}
          </p>
        </section>

        <aside
          data-testid="uc-contact"
          className="rounded-2xl bg-tz-espresso text-tz-cream p-8 lg:p-10 shadow-2xl border border-tz-espresso/30"
        >
          <div className="text-[10px] uppercase tracking-[0.28em] text-primary">
            {DEFAULTS.reach[locale] || DEFAULTS.reach.hr}
          </div>
          <h2 className="mt-2 font-serif text-3xl text-tz-cream">Glazbalarski obrt Mario Žmegač</h2>

          <ul className="mt-7 space-y-5 text-sm text-tz-cream/85">
            <li className="flex items-start gap-3">
              <Phone className="h-4 w-4 mt-0.5 text-primary shrink-0" strokeWidth={1.5} />
              <div>
                <a href={`tel:${phoneL}`} data-testid="uc-phone-landline" className="block font-medium text-tz-cream hover:text-primary transition-colors">
                  {phoneLDisplay}
                </a>
                <a href={`tel:${phoneM}`} data-testid="uc-phone-mobile" className="block mt-1 font-medium text-tz-cream hover:text-primary transition-colors">
                  {phoneMDisplay}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="h-4 w-4 mt-0.5 text-primary shrink-0" strokeWidth={1.5} />
              <a href={`mailto:${email}`} data-testid="uc-email" className="font-medium text-tz-cream hover:text-primary break-all transition-colors">
                {email}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="h-4 w-4 mt-0.5 text-primary shrink-0" strokeWidth={1.5} />
              <address data-testid="uc-address" className="not-italic text-tz-cream/85 leading-relaxed">
                {addr.map((line, idx) => <span key={idx} className="block">{line}</span>)}
              </address>
            </li>
          </ul>
        </aside>
      </main>

      {/* Footer with discreet admin link */}
      <footer className="px-5 lg:px-10 py-6 flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-tz-espresso/45">
        <span>© {new Date().getFullYear()} Tambure Žmegač</span>
        <Link
          to="/admin/login"
          data-testid="uc-admin-link"
          className="inline-flex items-center gap-1 hover:text-primary transition-colors"
        >
          {DEFAULTS.adminLink[locale] || DEFAULTS.adminLink.hr}
          <ArrowRight className="h-3 w-3" strokeWidth={1.5} />
        </Link>
      </footer>
    </div>
  );
}
